import * as React from 'react';

import { cn } from '../lib/utils';

interface InputProps extends React.ComponentProps<'input'> {
  error?: boolean;
  label?: string;
  helpText?: string;
  /** 내장 글자 수 카운터 표시 여부. 생략 시 maxLength 존재 여부에 따라 자동. */
  showCounter?: boolean;
  wrapperClassName?: string;
  /** 인풋 행 오른쪽에 렌더되는 트레일링 요소(예: 전송 버튼). Figma "Text Input/Base" 슬롯. */
  trailing?: React.ReactNode;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      error,
      label,
      helpText,
      showCounter,
      wrapperClassName,
      trailing,
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    const charCount =
      typeof value === 'string'
        ? value.length
        : typeof value === 'number'
          ? String(value).length
          : 0;

    const showCounterResolved = showCounter ?? maxLength !== undefined;
    const showBottomRow = !!helpText || showCounterResolved;

    return (
      <div
        className={cn(
          'gap-global-spacing-8 inline-flex w-full flex-col items-start justify-start',
          wrapperClassName
        )}
      >
        {label && (
          <div className='gap-global-spacing-4 inline-flex items-center self-stretch'>
            <span className='text-text-neutral-solid text-sm leading-4 font-semibold'>
              {label}
            </span>
          </div>
        )}
        <div className='gap-global-spacing-8 inline-flex items-center self-stretch'>
          {/* dir="auto": 입력 콘텐츠가 제 첫 강한 문자 기준으로 방향을 잡게 한다.
              RTL(아랍어) UI 안에서 한국어/라틴 입력이 우측정렬·커서반대로 되는 것 방지.
              {...props} 앞이라 호출자가 dir 을 넘기면 그 값이 우선한다.
              단, 비어 있으면 판단할 글자가 없어 LTR 로 잡히므로 안내 문구가 보이는 동안은
              화면 방향(html dir)을 따르게 덮는다 — 아랍어 안내 문구의 마침표가 앞에 붙던 문제(HD-1034). */}
          <input
            dir='auto'
            className={cn(
              'rounded-global-radius-20 bg-bg-neutral-subtle px-global-spacing-12 py-global-spacing-4 text-text-neutral-solid placeholder:text-text-neutral-weak flex min-h-9 w-full text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] disabled:cursor-not-allowed disabled:opacity-50',
              'rtl:placeholder-shown:[direction:rtl]',
              trailing && 'min-w-0',
              error
                ? 'outline-line-border-error focus-visible:outline-line-border-error focus-visible:shadow-[0px_0px_0px_3px_rgba(255,29,32,0.20)]'
                : 'outline-line-border-neutral-weak focus-visible:outline-line-outline-brand-primary-focused focus-visible:shadow-[0px_0px_0px_3px_rgba(196,150,255,0.20)]',
              className
            )}
            maxLength={maxLength}
            ref={ref}
            type={type}
            value={value}
            {...props}
          />
          {trailing}
        </div>
        {showBottomRow && (
          <div className='gap-global-spacing-10 px-global-spacing-4 inline-flex w-full items-start justify-end'>
            {helpText && (
              <p
                className={cn(
                  'flex-1 text-sm leading-4',
                  error ? 'text-bg-error-solid' : 'text-text-neutral-weak'
                )}
              >
                {helpText}
              </p>
            )}
            {showCounterResolved && (
              <span className='text-text-neutral-subtle text-end text-sm leading-4'>
                {charCount}/{maxLength}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
export type { InputProps };
